import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router'
import { Icon, Segment, Table } from 'semantic-ui-react'
import { RootState } from '../../../../../store/rootState'
import { IRouteParams } from '../../../../../types/routeParams'
import { IUserDetail } from '../../../../../types/user'
import { checkAdminStatus, checkRole } from '../../../../../utils/checkRole'
import { sortTasks } from '../../../../../utils/sort'
import * as BudgetActions from '../../../actions/budgetCostsActions'
import * as CostsActions from '../../../actions/directAndPassTroughCosts'
import ServiceProviderExternalBudgetTable from '../../../components/budget/service-provider/ServiceProviderExternalCostsTableComponent'
import { getBudgetCostsSelector } from '../../../selectors/budgetCostsSelector'
import { IProjectBudgetCost } from '../../../types/costs'
import { ICostSearch, ICostSorting, ICostsSortColumn, IDirectCost } from '../../../types/directAndPassTroughCosts'
import { IProject } from '../../../types/project'

interface StateProps {
  project: IProject
  costList: IProjectBudgetCost[]
  sortCosts: ICostSorting
  user: IUserDetail | null
  searchCostsValue: ICostSearch
  directCosts: IDirectCost[]
}

interface DispatchProps {
  setSortCosts: (sorting: ICostSorting) => void
  createBudgetCost: (projId: string | number, cost: IProjectBudgetCost) => Promise<IProjectBudgetCost>
  updateBudgetCost: (projId: string | number, cost: IProjectBudgetCost) => Promise<IProjectBudgetCost>
  deleteBudgetCost: (projId: string | number, cost: IProjectBudgetCost) => Promise<string | number | void>
  getDirectCosts: (prId: string | number, queryParams?: Record<string, string>) => void
}

type Props = StateProps & DispatchProps

const _externalHeader = [
  { text: 'Position', textAlign: 'left' as CanvasTextAlign },
  { text: 'Linked Position', textAlign: 'left' as CanvasTextAlign },
  { text: 'Supplier', textAlign: 'left' as CanvasTextAlign },
  { text: 'Quantity', textAlign: 'center' as CanvasTextAlign },
  { text: 'Unit Price', textAlign: 'right' as CanvasTextAlign },
  { text: 'Total Costs', textAlign: 'right' as CanvasTextAlign },
  { text: '', textAlign: 'right' as CanvasTextAlign }
]

const ServiceProviderExternalCostsContainer = (props: Props): JSX.Element => {
  const {
    project,
    sortCosts,
    user,
    costList,
    searchCostsValue,
    directCosts,
    createBudgetCost,
    setSortCosts,
    updateBudgetCost,
    deleteBudgetCost,
    getDirectCosts
  } = props

  const param = useParams() as IRouteParams

  const [sort, setSort] = useState(sortCosts.column)
  const [costListState, setCostListState] = useState(costList)

  useEffect(() => {
    let _costs = costList
    if (searchCostsValue.search !== '') {
      _costs = costList.filter(
        dc => dc.position.toLowerCase().includes(searchCostsValue.search.toLowerCase())
      )
    }
    setCostListState(_costs)
  }, [searchCostsValue, costList])

  const calcTotal = (costs: IProjectBudgetCost[]) => {
    const numberList = costs.map(a => a.unit_price && a.quantity ? +a.unit_price * +a.quantity : 0)
    const calc = numberList.reduce((a, b) => (a ? a : 0) + (b ? b : 0), 0)
    return calc ? calc : 0
  }

  const handleGetDirectCosts = () => {
    getDirectCosts(param.id)
  }

  const onClick = (sortBy: ICostsSortColumn['name']) => {
    const sorting = sortCosts
    sorting.column.direction = sortBy !== sortCosts.column.name ? 'asc' : sortCosts.column.direction === 'asc' ? 'desc' : 'asc'
    sorting.column.name = sortBy
    setSortCosts(sorting)
    setSort({ direction: sorting.column.direction, name: sorting.column.name })
  }

  const renderTableHeader = () => {
    return _externalHeader.map((h, idx) => {
      return (
        <Table.Cell key={idx} style={{ display: h.text === 'Position' ? 'flex' : '', alignItems: 'baseline' }}>
          {h.text === 'Position' ?
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: h.textAlign === 'center' ? 'center' : h.textAlign === 'left' ? 'flex-start' : 'flex-end',
                paddingLeft: '40px'
              }}
            >
              <div style={{ fontSize: '14px', color: '#747678' }}>{h.text}</div>
              <div
                style={{ display: 'flex', flexDirection: 'column' }}
                onClick={() => onClick(h.text.toLowerCase() as ICostsSortColumn['name'])}
              >
                <Icon
                  name='sort up'
                  style={{
                    color: '#747678',
                    opacity: sort.name === h.text.toLowerCase() && sort.direction === 'asc' ? 0.4 : 1,
                    fontSize: '10px'
                  }}
                />
                <Icon
                  name='sort down'
                  style={{
                    marginTop: '-10px',
                    color: '#747678',
                    opacity: sort.name === h.text.toLowerCase() && sort.direction === 'desc' ? 0.4 : 1,
                    fontSize: '10px'
                  }}
                />
              </div>
            </div> :
            <p style={{ textAlign: h.textAlign, color: '#747678' }}>{h.text}</p>}
        </Table.Cell>
      )
    })
  }

  const singleCluster = () => {
    const _costs = sortedCosts(costListState)
    return (
      <ServiceProviderExternalBudgetTable
        project={project}
        costs={_costs}
        calcTotalCost={calcTotal(_costs)}
        name='External Costs'
        directCosts={directCosts}
        createBudgetCost={createBudgetCost}
        updateBudgetCost={updateBudgetCost}
        deleteBudgetCost={deleteBudgetCost}
        getDirectCosts={handleGetDirectCosts}
      />
    )
  }

  const renderTable = () => {
    return (
      <Segment style={{ padding: '0 0 16px', margin: '0 0 1rem 0', border: 'none', borderRadius: '.57rem', boxShadow: '0 0 25px -5px #15151511' }}>
        <Table unstackable padded style={{ border: 'none' }} className='borderless-table-header custom-padding-large striped-table-with-collapse'>
          <Table.Header>
            <Table.Row>
              {renderTableHeader()}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {singleCluster()}
          </Table.Body>
        </Table>
      </Segment>
    )
  }

  const sortedCosts = (costs: IProjectBudgetCost[]) => {
    let sortedList = [...costs]
    if (sort.name === 'position') {
      sortedList = sortTasks(sortedList, 'position')
    }
    if (sort.direction === 'desc') {
      sortedList.reverse()
    }
    return sortedList.filter(c => c.position)
  }

  return (
    <React.Fragment>
      {user && (checkRole(user, [16]) || checkAdminStatus(user, project)) ? renderTable() : null}
    </React.Fragment>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    project: state.projectsState.project,
    costList: getBudgetCostsSelector(state),
    sortCosts: state.projectsState.sortCosts,
    user: state.authState.user,
    searchCostsValue: state.projectsState.searchCosts,
    directCosts: state.projectsState.directCosts
  }
}

const mapDispatchToProps = (dispatch: CostsActions.DirectAndPassTroughCostThunkDispatchType & BudgetActions.BudgetCostsThunkDispatchType): DispatchProps => {
  return {
    setSortCosts: sorting => dispatch(CostsActions.sortCosts(sorting)),
    createBudgetCost: (projId, cost) => dispatch(BudgetActions.createBudgetCost(projId, cost)),
    updateBudgetCost: (projId, cost) => dispatch(BudgetActions.updateBudgetCost(projId, cost)),
    deleteBudgetCost: (projId, cost) => dispatch(BudgetActions.deleteBudgetCost(projId, cost)),
    getDirectCosts: (prId, queryParams) => dispatch(CostsActions.getDirectCosts(prId, queryParams)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ServiceProviderExternalCostsContainer)
